import { Component, h, JSX, Host, Event, EventEmitter, State, Listen } from '@stencil/core';
import { MESSAGES, PATH } from 'src/components/constants';
import { store } from 'src/utils/store';
import {
  CountriesEnum,
  DesignEnum,
  LocalesEnum,
  ModuleEnum,
  RegionsEnum,
  RequestProductDocument,
  RequestProductMutation,
  RequestProductMutationVariables
} from 'src/generated/operations';
import { urlToFile } from 'src/utils/urlToFile';
import { BelimoError } from 'src/components/app-error-messages/type';
import { SalutationEnum } from 'shared/enums/SalutationEnum';
import { getGlobalAssetsPath } from 'src/utils/getGlobalAssetsPath';
import { createClickHandlerForImageOrPdfUpload } from 'src/utils/createClickHandlerForImageOrPdfUpload';

type InputNames = 'file1' | 'file2' | 'file3';
const ALL_INPUT_NAMES: InputNames[] = ['file1', 'file2', 'file3'];

@Component({
  tag: 'request-form',
  shadow: false
})
export class RequestForm {
  @Event() belimoError?: EventEmitter<BelimoError>;
  @State() isVisible = false;
  @State() isNotificationVisible = false;
  @State() inProgress = false;
  @State() privacyChecked = false;
  @State() countryCode: CountriesEnum;
  @State()
  public file1: File | null = null;
  @State()
  public file2: File | null = null;

  @State()
  public file3: File | null = null;
  @State()
  private phoneIsValid: boolean = true;

  private inputs: Record<string, HTMLInputElement> = {};

  private form!: HTMLFormElement;
  private clickedFileInputName: InputNames | null = null;

  private phone: string | null = null;
  private handleClick: (e: MouseEvent) => void = () => null;

  @Listen('requestFormOpen', {target: 'document'})
  public async watchOpen({detail: dataUrl}: { detail: string }): Promise<void> {
    this.isVisible = true;
    this.inProgress = false;
    if (dataUrl && dataUrl.length > 0) {
      this.file1 = await urlToFile(dataUrl, 'image1');
    }
  }

  @Listen('processFile', {target: 'document'})
  public async watchImage({detail}: { detail: string }): Promise<void> {
    if (this.clickedFileInputName === null) {
      return;
    }
    this[this.clickedFileInputName] = await urlToFile(detail, this.clickedFileInputName);
    this.clickedFileInputName = null;
  }

  public async componentWillLoad(): Promise<void> {
    this.handleClick = createClickHandlerForImageOrPdfUpload();
    this.countryCode = store.state.currentCountry;
    store.onChange('currentCountry', () => {
      this.countryCode = store.state.currentCountry;
    })
  }

  private getFile(form: FormData, name: InputNames): File | null {
    if (this[name]) {
      return this[name];
    }
    if (form.has(name)) {
      return form.get(name) as File;
    }
    return null;
  }

  private getEMEAValveInformationLink(designType: string, currentLocale: LocalesEnum): string {
    const isGlobeValve = designType === DesignEnum.GlobeValve;
    const isDeCh = currentLocale === LocalesEnum.DeCh;

    return `https://www.belimo.com/mam/europe/technical-documentation/technical-leaflets/water_solutions/belimo_retrofit-${
        isGlobeValve ? 'globe' : 'rotary'
      }-valve-adapter_data-collection-sheet_${
        isDeCh ? 'de-ch' : 'en-gb'
      }.pdf`;
  }

  public onSubmit(e: any): void {
    e.preventDefault();
    this.inProgress = true;
    const f = new FormData(e.target);
    const file1 = this.getFile(f, 'file1');
    const file2 = this.getFile(f, 'file2');
    const file3 = this.getFile(f, 'file3');
    const form = {
      region: store.state.currentRegion,
      manufacturer: f.get('manufacturer').toString(),
      productName: f.get('productName').toString(),
      category: f.get('category').toString(),
      email: f.get('email').toString(),
      country: f.get('country').toString(),
      salutation: f.get('salutation').toString(),
      firstName: f.get('firstName').toString(),
      lastName: f.get('lastName').toString(),
      companyName: f.get('companyName').toString(),
      ...(this.phone ? {phone: this.phone} : {}),
      ...(f.get('description').toString().length > 0 ? {description: f.get('description').toString()} : {}),
      ...(file1 && (file1 as File).size > 0 ? {file1: file1 as File} : {}),
      ...(file2 && (file2 as File).size > 0 ? {file2: file2 as File} : {}),
      ...(file3 && (file3 as File).size > 0 ? {file3: file3 as File} : {}),
    };
    store.state.apollo.mutate<RequestProductMutation, RequestProductMutationVariables>({
      mutation: RequestProductDocument,
      variables: {form}
    }).then(() => {
      this.isVisible = false;
      this.inProgress = false;
      this.reset();
      this.isNotificationVisible = true;
    }).catch((err) => {
      this.belimoError?.emit({ type: 'unknown', cause: err.message });
      this.inProgress = false;
    })
  }

  public render(): JSX.Element {
    const [route, deviceType] = window.location.pathname.replace(/\//g, ' ').trim().split(' ');

    const isFindByDeviceRoute = route === PATH.FIND_BY_DESIGN;

    const placeholder = store.state.config
      && store.state.config.i18n.REQUEST_PRODUCT_FORM_DESCRIPTION_PLACEHOLDER || '';
    return (<Host>
      {this.isNotificationVisible && <app-notification onBelimoNotificationClose={() => {
        this.isNotificationVisible = false;
      }}>
        <span slot="header"><belimo-i18n messageId={MESSAGES.SUCCESS}/></span>
        <span><belimo-i18n messageId={MESSAGES.REQUEST_PRODUCT_FORM_SUCCESS_TEXT}/></span>
      </app-notification>}
      {this.isVisible && <form ref={(el) => this.form = el} onSubmit={(e) => this.onSubmit(e)} class="popup">
        <div class="popup__overlay"/>
        <div class="popup__window">
          {this.inProgress &&<div class="popup__cover"><div class="loader" /></div>}
          <div class="popup__close" onClick={() => {
            this.isVisible = false;
            this.reset();
          }}>
            <belimo-svg-sprite svgId={'x'}/>
          </div>
          <div class="popup__header">
            <div class="popup__header-icon">
              <belimo-svg-sprite svgId={'support'}/>
            </div>
            <h1 class="popup__header-title">
              <belimo-i18n messageId={MESSAGES.REQUEST_PRODUCT_FORM_TITLE}/>
            </h1>
            <h3 class="popup__header-subtitle">
              <belimo-i18n messageId={MESSAGES.REQUEST_PRODUCT_FORM_SUBTITLE}/>
            </h3>
          </div>
          <div class="popup__content">
            <div class="popup__request">
              <div class="popup__request-col1">
                <div class="form-group">
                  <div class="form-group__label"><label>
                    <belimo-i18n messageId={MESSAGES.company}/>
                    <span>(<belimo-i18n messageId={MESSAGES.REQUIRED}/>)</span></label></div>
                  <div class="form-group__control">
                    <div class="textInput"><input name="manufacturer" class="textInput" required={true}/></div>
                  </div>
                </div>
                <div class="form-group">
                  <div class="form-group__label"><label>
                    <belimo-i18n messageId={MESSAGES.PRODUCT_NAME}/>
                    <span>(<belimo-i18n messageId={MESSAGES.REQUIRED}/>)</span></label></div>
                  <div class="form-group__control">
                    <div class="textInput"><input name="productName" class="textInput" required={true}/></div>
                  </div>
                </div>

                <div class="form-group">
                  <div class="form-group__label"><label>
                    <belimo-i18n messageId={MESSAGES.CATEGORY}/>
                    <span>(<belimo-i18n messageId={MESSAGES.REQUIRED}/>)</span></label></div>
                  <div class="form-group__control">
                    <select name="category" class="selectInput" required={true}>
                      <option selected disabled value=''>
                        <belimo-i18n messageId={MESSAGES.CHOOSE_CATEGORY}/>
                      </option>
                      <option value={ModuleEnum.Air}>
                        <belimo-i18n messageId={ModuleEnum.Air}/>
                      </option>
                      <option value={ModuleEnum.Water}>
                        <belimo-i18n messageId={ModuleEnum.Water}/>
                      </option>
                      <option value={ModuleEnum.Sensor}>
                        <belimo-i18n messageId={ModuleEnum.Sensor}/>
                      </option>
                    </select>
                  </div>
                </div>

                <div class="form-group">
                  <div class="form-group__label"><label>
                    <belimo-i18n messageId={MESSAGES.COUNTRY}/>
                    <span>(<belimo-i18n messageId={MESSAGES.REQUIRED}/>)</span></label></div>
                  <div class="form-group__control">
                    <select name="country" class="selectInput" required={true}
                      onChange={(event) => {
                        const { target } = event;
                        this.countryCode = (target as HTMLSelectElement).value as CountriesEnum
                      }}
                    >
                      {store.state.config.countries.map((item) => {
                        return <option value={item.code}
                                       selected={item.code === this.countryCode}>
                          {item.data.countryName}
                        </option>
                      })}
                    </select>
                  </div>
                </div>

                <div class="form-group">
                  <div class="form-group__label"><label>
                    <belimo-i18n messageId={MESSAGES.REQUEST_PRODUCT_FORM_SALUTATION}/>
                    <span>(<belimo-i18n messageId={MESSAGES.REQUIRED}/>)</span></label></div>
                  <div class="form-group__control">
                    <select name="salutation" class="selectInput" required={true}>
                      <option value={SalutationEnum.MALE}>
                        <belimo-i18n messageId={MESSAGES.REQUEST_PRODUCT_FORM_MALE}/>
                      </option>
                      <option value={SalutationEnum.FEMALE}>
                        <belimo-i18n messageId={MESSAGES.REQUEST_PRODUCT_FORM_FEMALE}/>
                      </option>
                      <option value={SalutationEnum.PREFER_NOT_SAY}>
                        <belimo-i18n messageId={MESSAGES.REQUEST_PRODUCT_FORM_NO_INFO}/>
                      </option>
                    </select>
                  </div>
                </div>

                <div class="popup__2column">
                  <div class="form-group">
                    <div class="form-group__label"><label>
                      <belimo-i18n messageId={MESSAGES.REQUEST_PRODUCT_FORM_FIRST_NAME}/>
                    </label></div>
                    <div class="form-group__control">
                      <div class="textInput"><input name="firstName" class="textInput"/></div>
                    </div>
                  </div>
                  <div class="form-group">
                    <div class="form-group__label"><label>
                      <belimo-i18n messageId={MESSAGES.REQUEST_PRODUCT_FORM_LAST_NAME}/>
                      <span>(<belimo-i18n messageId={MESSAGES.REQUIRED}/>)</span></label></div>
                    <div class="form-group__control">
                      <div class="textInput"><input name="lastName" class="textInput" required={true}/></div>
                    </div>
                  </div>
                </div>

                <div class="form-group">
                  <div class="form-group__label"><label>
                    <belimo-i18n messageId={MESSAGES.REQUEST_PRODUCT_FORM_COMPANY}/>
                    <span>(<belimo-i18n messageId={MESSAGES.REQUIRED}/>)</span></label></div>
                  <div class="form-group__control">
                    <div class="textInput"><input name="companyName" class="textInput" required={true}/></div>
                  </div>
                </div>

                <div class="popup__2column">
                  <div class="form-group">
                    <div class="form-group__label"><label>
                      <belimo-i18n messageId={MESSAGES.EMAIL}/>
                      <span>(<belimo-i18n messageId={MESSAGES.REQUIRED}/>)</span></label></div>
                    <div class="form-group__control">
                      <div class="textInput"><input name="email" class="textInput" type="email" required={true}/></div>
                    </div>
                  </div>
                  <div class="form-group">
                    <div class="form-group__label"><label>
                      <belimo-i18n messageId={MESSAGES.PHONE}/>
                      <span>(<belimo-i18n messageId={MESSAGES.REQUIRED}/>)</span>
                    </label></div>
                    <div class="form-group__control">
                      <tel-input class="popup__phone" name="phone"
                                 countryCode={this.countryCode}
                                 onTelInputChange={({detail}) => {
                                   this.phone = detail;
                                 }}
                                 onTelInputValid={({detail}) => {
                                   this.phoneIsValid = detail;
                                 }}/>
                    </div>
                  </div>
                </div>
                <div class="form-group">
                  <div class="form-group__label"><label>
                    <belimo-i18n messageId={MESSAGES.DESCRIPTION}/>
                  </label></div>
                  <div class="form-group__control"><textarea
                    name="description" class="textArea"
                    placeholder={placeholder}
                  />
                  </div>
                </div>

                {isFindByDeviceRoute && store.state.currentRegion !== RegionsEnum.Am && (
                  <div class="form-group">
                    <div class="form-group__label"><label>
                      <belimo-i18n messageId={MESSAGES.FBD_Product_Not_Found_Description}/>
                    </label></div>

                    <div class="form-group__control">
                      <a 
                        class="btn btn--primary form-group__button" 
                        href={this.getEMEAValveInformationLink(deviceType, store.state.currentLocale)} 
                        download
                        target='_blank'
                      >
                        <belimo-i18n messageId={MESSAGES.FBD_Product_Not_Found_Download_Button}/>
                      </a>
                    </div>
                  </div>
                )}

                <div class="form-group">
                  <div class="form-group__control">
                    <input type="checkbox" checked={this.privacyChecked} onChange={() => this.privacyChecked = !this.privacyChecked } />
                    &nbsp;
                    <belimo-i18n messageId={MESSAGES.REQUEST_PRODUCT_FORM_TEXT}/>
                    &nbsp;
                    <belimo-privacy-policy />
                  </div>
                </div>
              </div>
              <div class="popup__request-col2">
                <div class="form-group">
                  <div class="form-group__label"><label>
                    <belimo-i18n messageId={MESSAGES.PHOTO}/>
                  </label></div>
                  <div class="popup__photo">
                    {ALL_INPUT_NAMES.map((name, index) => this.renderInput(name, index))}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="popup__footer">
            <button type="reset" class="btn btn--secondary" onClick={() => {
              this.isVisible = false
              this.reset();
            }}>
              <belimo-i18n messageId={MESSAGES.CANCEL}/>
            </button>
            <button type="submit" class="btn btn--primary" disabled={this.inProgress || this.phoneIsValid === false || this.privacyChecked === false}>
              <belimo-i18n messageId={MESSAGES.SAVE}/>
            </button>
          </div>
        </div>
      </form>}
    </Host>);
  }

  private reset(): void {
    this.form.reset();
    this.file1 = null;
    this.file2 = null;
    this.file3 = null;
    this.phone = '';
  }

  private renderInput(name: InputNames, index: number): JSX.Element {
    const inputField = `${name}Input`;

    const [route] = window.location.pathname.replace(/\//g, ' ').trim().split(' ');

    const isFindByDeviceRoute = route === PATH.FIND_BY_DESIGN;

    index += 1;
    return (
      <label htmlFor={name} class={{'active': this[name] && this[name].size > 0}} onClick={(e) => {
        if (this[name] && this[name].size > 0) {
          e.preventDefault();
          return;
        }
        this.clickedFileInputName = name;
        this.handleClick(e);
      }}>
        <input 
          type="file" 
          class="inputfile" 
          accept="image/*,.pdf"
          ref={(ref) => this.inputs[inputField] = ref}
          id={name} name={name}
          onChange={(e) => this[name] = (e.target as HTMLInputElement).files.item(0)}
        />
        {(this[name] === null || this[name] && this[name].size === 0) && <div class="popup__photo-select">
          <div class="popup__photo-title">
            <belimo-i18n messageId={isFindByDeviceRoute && store.state.currentRegion !== RegionsEnum.Am ? MESSAGES.FBD_Product_Not_Found_Upload_Description : MESSAGES.FILE_TITLE}/>
            &nbsp;{index}</div>
          <div class="popup__photo-icon">
            <belimo-svg-sprite svgId={'add'}/>
          </div>
        </div>}

        {this[name] && this[name].size > 0 && <div class="popup__photo-active">
          <div class="popup__photo-img">
            {this[name].type === 'application/pdf' ? (
              <div class="popup__object">
                <img class="popup__object-icon" src={getGlobalAssetsPath('images/pdf.svg')}/>
              </div>
            ) : (
              <img src={URL.createObjectURL(this[name])}/>
            )}
          </div>
          <div class="popup__photo-close" onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (this.inputs[inputField]) {
              this.inputs[inputField].value = '';
            }
            this[name] = null;
          }}>
            <belimo-svg-sprite svgId={'x'}/>
          </div>
        </div>}
      </label>
    )
  }
}
